import { useState, useEffect, useCallback } from "react";
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
} from "react-icons/fa";
import Slider from "./Slider";
import "../../assets/css/admin.css";
import { Link } from "react-router-dom";
import BlogModal from "./CreateBlogModal";
import { deleteBlog, getBlogsPage } from "../../Apis/blogs";

function DeleteConfirmationModal({ onClose, onConfirm, loading }) {
  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal-container">
        <div className="admin-modal-header">Confirm Delete</div>
        <div className="admin-modal-body">
          <p>Are you sure you want to delete this blog post? This action cannot be undone.</p>
        </div>
        <div className="admin-modal-footer">
          <button
            type="button"
            onClick={onClose}
            className="admin-modal-btn admin-modal-btn-cancel"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="admin-modal-btn admin-modal-btn-submit admin-modal-btn-danger"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Blogs() {
  const [showModal, setShowModal] = useState(false);
  const [blogId, setBlogId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [blogs, setBlogs] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBlogs, setTotalBlogs] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getBlogsPage(page, perPage);
      setBlogs(res.data?.data?.blogs || []);
      setTotalPages(res.data?.data?.pages || 1);
      setTotalBlogs(res.data?.data?.total || 0);
    } catch (err) {
      setError("Failed to load blogs. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [page, perPage]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  const handleDelete = async () => {
    if (!blogToDelete) return;
    
    setDeleteLoading(true);
    try {
      await deleteBlog(blogToDelete);
      setShowDeleteModal(false);
      setBlogToDelete(null);
      if (blogs.length === 1 && page > 1) {
        setPage((prev) => prev - 1);
      } else {
        fetchBlogs();
      }
    } catch (error) {
      // Handle delete error
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteClick = (blogId) => {
    setBlogToDelete(blogId);
    setShowDeleteModal(true);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
      setPage(newPage);
    }
  };

  
  return (
    <div className="admin-dashboard">
      <Slider />

      {/* Main Content */}
      <div className="admin-main-content">
        {/* Breadcrumb */}
        <div className="admin-breadcrumb">
          <div className="admin-breadcrumb-text">Admin / Blogs</div>
        </div>

        {/* Main Content Area */}
        <div className="admin-content-area">
          <div className="admin-blogs-header">
            <h2 className="admin-blogs-title">Blogs Management</h2>
            <button
              onClick={() => {
                setBlogId(null);
                setShowModal(true);
              }}
              className="admin-create-btn"
            >
              Create New Blog
            </button>
          </div>

          {error && (
            <div className="admin-error-message">
              {error}
            </div>
          )}

          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th className="admin-table-id">ID</th>
                  <th>Title & Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr className="admin-loading-row">
                    <td colSpan="3">Loading blogs...</td>
                  </tr>
                ) : blogs.length > 0 ? (
                  blogs.map((blog) => (
                    <tr key={blog.blog_id}>
                      <td className="admin-table-id">{blog.blog_id}</td>
                      <td>
                        <div className="admin-blog-title">{blog.title}</div>
                        <div className="admin-blog-date">
                          Created {new Date(blog.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td>
                        <div className="admin-actions">
                          <Link
                            to={`/admin/blog-details/${blog.slug}`}
                            className="admin-action-btn admin-view-btn"
                          >
                            <FaEye /> View
                          </Link>
                          <button
                            onClick={() => {
                              setBlogId(blog.blog_id);
                              setShowModal(true);
                            }}
                            className="admin-action-btn admin-edit-btn"
                          >
                            <FaEdit /> Edit
                          </button>
                          <button
                            onClick={() => handleDeleteClick(blog.blog_id)}
                            className="admin-action-btn admin-delete-btn"
                          >
                            <FaTrash /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="admin-no-data-row">
                    <td colSpan="3">No blogs found.</td>
                  </tr>
                )}
              </tbody>
            </table>

            {totalPages > 0 && (
              <div className="admin-pagination">
                <div className="admin-pagination-info">
                  Showing {(page - 1) * perPage + 1} to{" "}
                  {Math.min(page * perPage, totalBlogs)} of {totalBlogs} blogs (Page {page} of {totalPages})
                </div>
                <div className="admin-pagination-controls">
                  {(() => {
                    const maxVisiblePages = 10;
                    let startPage = Math.max(1, page - 2);
                    let endPage = Math.min(totalPages, page + 2);

                    if (totalPages <= maxVisiblePages) {
                      startPage = 1;
                      endPage = totalPages;
                    } else if (page <= 3) {
                      endPage = maxVisiblePages;
                    } else if (page >= totalPages - 2) {
                      startPage = totalPages - maxVisiblePages + 1;
                    }

                    return (
                      <>
                        <button
                          onClick={() => handlePageChange(1)}
                          disabled={page === 1}
                          className="admin-pagination-btn"
                        >
                          <FaAngleDoubleLeft />
                        </button>
                        <button
                          onClick={() => handlePageChange(page - 1)}
                          disabled={page === 1}
                          className="admin-pagination-btn"
                        >
                          <FaChevronLeft />
                        </button>
                        {Array.from(
                          { length: endPage - startPage + 1 },
                          (_, i) => startPage + i
                        ).map((pageNum) => (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`admin-pagination-btn ${
                              page === pageNum ? "active" : ""
                            }`}
                          >
                            {pageNum}
                          </button>
                        ))}
                        <button
                          onClick={() => handlePageChange(page + 1)}
                          disabled={page === totalPages}
                          className="admin-pagination-btn"
                        >
                          <FaChevronRight />
                        </button>
                        <button
                          onClick={() => handlePageChange(totalPages)}
                          disabled={page === totalPages}
                          className="admin-pagination-btn"
                        >
                          <FaAngleDoubleRight />
                        </button>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <BlogModal
          blogId={blogId}
          onClose={() => {
            setShowModal(false);
            fetchBlogs();
          }}
        />
      )}

      {showDeleteModal && (
        <DeleteConfirmationModal
          onClose={() => {
            setShowDeleteModal(false);
            setBlogToDelete(null);
          }}
          onConfirm={handleDelete}
          loading={deleteLoading}
        />
      )}
    </div>
  );
}
